import { useEffect, useState } from 'react';
import type { ComponentPropsWithoutRef } from 'react';

import type { ColorType } from '@lib/types';
import { cn, cva, type VariantProps, setThisClass } from '@lib/utils';

const animatedTickVariants = cva(
  [
    // Layout
    'inline-flex items-center justify-center',
    // Color (stroke-current on SVG inherits from text color)
    'text-this-solid',
  ],
  {
    variants: {
      size: {
        sm: '[--tick-size:24px]',
        md: '[--tick-size:32px]',
        lg: '[--tick-size:48px]',
        xl: '[--tick-size:64px]',
      },
    },
    defaultVariants: {
      size: 'md',
    },
  }
);

type AnimatedTickProps = {
  show?: boolean;
  color?: ColorType;
  strokeWidth?: number;
  duration?: number;
  delay?: number;
  onComplete?: () => void;
} & VariantProps<typeof animatedTickVariants> &
  Omit<ComponentPropsWithoutRef<'div'>, 'color'>;

function AnimatedTick({
  show = true,
  color = 'success',
  size,
  strokeWidth = 3,
  duration = 400,
  delay = 0,
  className,
  onComplete,
  ...rest
}: AnimatedTickProps) {
  const [isAnimating, setIsAnimating] = useState(false);

  // Size value from variant
  const sizeMap = { sm: 24, md: 32, lg: 48, xl: 64 };
  const svgSize = sizeMap[size ?? 'md'];
  const center = svgSize / 2;
  const radius = center - strokeWidth;
  const circumference = 2 * Math.PI * radius;

  // Checkmark path points (calculated based on size)
  const checkPath = `M ${svgSize * 0.25} ${svgSize * 0.5} L ${svgSize * 0.42} ${svgSize * 0.67} L ${svgSize * 0.75} ${svgSize * 0.35}`;
  const checkLength = svgSize * 0.8;

  useEffect(() => {
    if (show) {
      const delayTimer = setTimeout(() => {
        setIsAnimating(true);
      }, delay);

      const completeTimer = setTimeout(
        () => {
          onComplete?.();
        },
        delay + duration * 2
      );

      return () => {
        clearTimeout(delayTimer);
        clearTimeout(completeTimer);
      };
    } else {
      setIsAnimating(false);
    }
  }, [show, delay, duration, onComplete]);

  if (!show) return null;

  return (
    <div className={cn(animatedTickVariants({ size }), setThisClass(color), className)} {...rest}>
      <svg
        width={svgSize}
        height={svgSize}
        viewBox={`0 0 ${svgSize} ${svgSize}`}
        className="stroke-current"
      >
        {/* Circle */}
        <circle
          cx={center}
          cy={center}
          r={radius}
          fill="none"
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          style={{
            strokeDasharray: circumference,
            strokeDashoffset: isAnimating ? 0 : circumference,
            transition: `stroke-dashoffset ${duration}ms ease-out`,
            transform: 'rotate(-90deg)',
            transformOrigin: 'center',
          }}
        />
        {/* Checkmark */}
        <path
          d={checkPath}
          fill="none"
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{
            strokeDasharray: checkLength,
            strokeDashoffset: isAnimating ? 0 : checkLength,
            transition: `stroke-dashoffset ${duration}ms ease-out ${duration * 0.5}ms`,
          }}
        />
      </svg>
    </div>
  );
}

AnimatedTick.displayName = 'AnimatedTick';

export { AnimatedTick };
export type { AnimatedTickProps };
